import type { ReactNode } from "react";
import { AlertTriangle, Info, CheckCircle, XCircle } from "lucide-react";

type AlertVariant = "warning" | "danger" | "info" | "success";

interface AlertProps {
  variant: AlertVariant;
  children: ReactNode;
  className?: string;
  id?: string;
  announce?: boolean;
}

const config: Record<
  AlertVariant,
  {
    icon: typeof AlertTriangle;
    border: string;
    bg: string;
    iconColor: string;
    text: string;
  }
> = {
  warning: {
    icon: AlertTriangle,
    border: "border-warning/30",
    bg: "bg-warning/10",
    iconColor: "text-warning",
    text: "text-amber-700 dark:text-warning-light",
  },
  danger: {
    icon: XCircle,
    border: "border-danger/30",
    bg: "bg-danger/10",
    iconColor: "text-danger",
    text: "text-danger",
  },
  info: {
    icon: Info,
    border: "border-info/30",
    bg: "bg-info/10",
    iconColor: "text-info",
    text: "text-info",
  },
  success: {
    icon: CheckCircle,
    border: "border-success/30",
    bg: "bg-success/10",
    iconColor: "text-success",
    text: "text-success",
  },
};

export function Alert({ variant, children, className = "", id, announce = true }: AlertProps) {
  const { icon: Icon, border, bg, iconColor, text } = config[variant];

  return (
    <div
      id={id}
      role={announce ? "alert" : undefined}
      className={`flex items-start gap-3 rounded-lg border ${border} ${bg} p-3 ${className}`}
    >
      <Icon size={16} className={`mt-0.5 shrink-0 ${iconColor}`} />
      <div className={`text-xs font-medium ${text}`}>{children}</div>
    </div>
  );
}
